/**
 * Interrupt-aware audio queue with monotonic event_id gating.
 *
 * VELLA_2_0_MASTER_BUILD_PLAN.md §3.2:
 *   Every TTS audio chunk is tagged with a monotonic `event_id` produced by
 *   the backend. The client maintains `lastInterruptTimestamp`. On barge-in,
 *   we bump the timestamp — any chunk with `event_id <= lastInterruptTimestamp`
 *   is dropped (both from the queue, and from any late-arriving network
 *   chunks for the same turn).
 *
 * VELLA_2_0_VOICE_AUDIT_AND_FIXES.md §1.3:
 *   Full client-reported `played_chars` reporting is Phase 6 scope. Here in
 *   Phase 5 we only track the highest event_id that began playback, so the
 *   Phase 6 work has something to reference. We do NOT do alignment math.
 *
 * ARCHITECTURE NOTE:
 *   LiveKit's RemoteAudioTrack (livekitClient.js `agentAudio` event) is a
 *   continuous media stream — not chunked. This queue is for the sidecar
 *   path: per-chunk audio blobs delivered via a LiveKit DataChannel message
 *   carrying `{ type: 'audio_chunk', event_id, alignment, audio }`. That
 *   wire format isn't emitted by the backend yet — Phase 5 lays the
 *   plumbing; Agent F / flashStream integration wires it up.
 *
 * DESIGN:
 *   - Pure JS, framework-agnostic (no React).
 *   - Sequential playback (one chunk at a time). Phase 6 may parallel-decode
 *     but sequential playback order is preserved for coherent speech.
 *   - Uses HTMLAudioElement + URL.createObjectURL (matches the existing
 *     `<audio>` element approach in livekitClient.js / TalkToVella.jsx and
 *     avoids adding a Web Audio decode path prematurely).
 *   - `bumpInterruptTimestamp` is the barge-in hook. It:
 *       (a) updates the gate so future pushes with event_id <= gate drop,
 *       (b) removes queued (not-yet-playing) chunks with event_id <= gate.
 *     It does NOT truncate the in-flight chunk mid-playback — Phase 6 adds
 *     that via alignment-aware mid-chunk truncation.
 */

/**
 * @typedef {Object} AudioChunk
 * @property {Blob|ArrayBuffer|Uint8Array} audio  Playable audio payload (MP3/OGG/WAV bytes).
 * @property {number} event_id                    Monotonic id issued by backend.
 * @property {boolean} [is_filler]                Optional: contextual-filler chunks (plan §3.7).
 * @property {Object}  [alignment]                Flash v2.5 alignment (Phase 6 will use).
 */

/**
 * @typedef {Object} AudioQueueOptions
 * @property {HTMLAudioElement} [audioElement]                  Hidden <audio> to play into. If absent, one is created.
 * @property {(eventId: number) => void} [onChunkDropped]       Called for every chunk dropped by the gate.
 * @property {(eventId: number) => void} [onChunkStarted]       Called when a chunk starts playback.
 * @property {(eventId: number) => void} [onChunkEnded]         Called when a chunk finishes playback.
 * @property {(err: Error, eventId: number) => void} [onError]  Called on playback errors.
 */

/**
 * Create an audio queue.
 *
 * @param {AudioQueueOptions} [opts]
 */
export function createAudioQueue(opts = {}) {
  const {
    audioElement,
    onChunkDropped,
    onChunkStarted,
    onChunkEnded,
    onError,
  } = opts;

  // ── State ─────────────────────────────────────────────────────────────
  /** @type {AudioChunk[]} FIFO of chunks waiting to play. */
  const queue = [];

  /** @type {HTMLAudioElement | null} */
  const audioEl = resolveAudioElement(audioElement);

  /**
   * Gate: any chunk with `event_id <= lastInterruptTimestamp` is dropped.
   * Starts at -1 so all non-negative event_ids pass initially.
   */
  let lastInterruptTimestamp = -1;

  /** The event_id of the chunk currently playing (or most recently played). */
  let playedEventId = -1;

  /** The chunk currently playing, if any. */
  /** @type {AudioChunk | null} */
  let activeChunk = null;

  /** Current ObjectURL so we can revoke it on end/clear. */
  let activeObjectUrl = null;

  let destroyed = false;

  // ── Helpers ───────────────────────────────────────────────────────────
  function safeCall(fn, ...args) {
    if (typeof fn !== 'function') return;
    try { fn(...args); }
    catch (err) { /* swallow — consumer callbacks are best-effort */
      if (typeof console !== 'undefined') console.warn('[audioQueue] listener threw:', err);
    }
  }

  function toObjectUrl(audio) {
    // Blob → URL directly. ArrayBuffer / Uint8Array → wrap in Blob first.
    if (typeof Blob !== 'undefined' && audio instanceof Blob) {
      return URL.createObjectURL(audio);
    }
    // Normalize binary views / buffers to a Blob.
    let parts;
    if (audio instanceof ArrayBuffer) {
      parts = [audio];
    } else if (audio && ArrayBuffer.isView(audio)) {
      parts = [audio.buffer.slice(audio.byteOffset, audio.byteOffset + audio.byteLength)];
    } else {
      throw new TypeError('[audioQueue] unsupported audio payload type');
    }
    // mpeg is a safe default for Flash v2.5 MP3 output; backend may send
    // other mimes — caller can pre-wrap as a Blob with the right type.
    const blob = new Blob(parts, { type: 'audio/mpeg' });
    return URL.createObjectURL(blob);
  }

  function revokeActiveUrl() {
    if (activeObjectUrl) {
      try { URL.revokeObjectURL(activeObjectUrl); } catch (_) { /* noop */ }
      activeObjectUrl = null;
    }
  }

  function playNext() {
    if (destroyed) return;
    if (activeChunk) return;         // already playing
    const next = queue.shift();
    if (!next) return;               // nothing to play

    // Re-check the gate: chunk might have become stale while in flight.
    if (next.event_id <= lastInterruptTimestamp) {
      safeCall(onChunkDropped, next.event_id);
      playNext();
      return;
    }

    activeChunk = next;
    playedEventId = next.event_id;

    if (!audioEl) {
      // No audio element available (e.g., SSR or tests not providing one).
      // Treat as instant-complete so queue drains deterministically.
      safeCall(onChunkStarted, next.event_id);
      safeCall(onChunkEnded, next.event_id);
      activeChunk = null;
      playNext();
      return;
    }

    let url;
    try {
      url = toObjectUrl(next.audio);
    } catch (err) {
      safeCall(onError, /** @type {Error} */ (err), next.event_id);
      activeChunk = null;
      playNext();
      return;
    }

    activeObjectUrl = url;
    try {
      audioEl.src = url;
    } catch (err) {
      safeCall(onError, /** @type {Error} */ (err), next.event_id);
      revokeActiveUrl();
      activeChunk = null;
      playNext();
      return;
    }

    safeCall(onChunkStarted, next.event_id);

    // play() returns a Promise in modern browsers; older impls / test mocks
    // may not. Guard both.
    let started;
    try { started = audioEl.play && audioEl.play(); }
    catch (err) {
      safeCall(onError, /** @type {Error} */ (err), next.event_id);
      handleEnded();
      return;
    }
    if (started && typeof started.catch === 'function') {
      started.catch((err) => safeCall(onError, err, next.event_id));
    }
  }

  function handleEnded() {
    const ended = activeChunk;
    revokeActiveUrl();
    activeChunk = null;
    if (ended) safeCall(onChunkEnded, ended.event_id);
    playNext();
  }

  function handleError(evt) {
    const id = activeChunk ? activeChunk.event_id : -1;
    const err = (evt && evt.error) || new Error('audio element error');
    safeCall(onError, err, id);
    handleEnded();
  }

  if (audioEl && typeof audioEl.addEventListener === 'function') {
    audioEl.addEventListener('ended', handleEnded);
    audioEl.addEventListener('error', handleError);
  }

  // ── Public API ────────────────────────────────────────────────────────

  /**
   * Enqueue a chunk. Dropped synchronously if event_id <= gate.
   * @param {AudioChunk} chunk
   */
  function push(chunk) {
    if (destroyed) return;
    if (!chunk || typeof chunk.event_id !== 'number') {
      if (typeof console !== 'undefined') {
        console.warn('[audioQueue] push: chunk missing numeric event_id', chunk);
      }
      return;
    }
    if (chunk.event_id <= lastInterruptTimestamp) {
      safeCall(onChunkDropped, chunk.event_id);
      return;
    }
    queue.push(chunk);
    if (!activeChunk) playNext();
  }

  /**
   * Bump the interrupt gate. Any queued chunks with event_id <= the new gate
   * are removed (and `onChunkDropped` fires for each). The in-flight chunk
   * continues to play — Phase 6 will add mid-chunk truncation.
   *
   * @param {number} eventId The highest event_id that has been "played"
   *                         (per audit §1.3, currently just the tracked id).
   */
  function bumpInterruptTimestamp(eventId) {
    if (typeof eventId !== 'number' || !Number.isFinite(eventId)) return;
    if (eventId <= lastInterruptTimestamp) return;    // monotonic only
    lastInterruptTimestamp = eventId;

    // Drop stale queued chunks. Walk + rebuild so we preserve FIFO order
    // for survivors.
    if (queue.length) {
      const survivors = [];
      for (const c of queue) {
        if (c.event_id <= lastInterruptTimestamp) {
          safeCall(onChunkDropped, c.event_id);
        } else {
          survivors.push(c);
        }
      }
      queue.length = 0;
      for (const c of survivors) queue.push(c);
    }
  }

  /**
   * Stop any in-flight playback and drop everything queued. Does NOT reset
   * the interrupt gate — a subsequent session should construct a new queue.
   */
  function clear() {
    // Drain queued chunks.
    const dropped = queue.splice(0, queue.length);
    for (const c of dropped) safeCall(onChunkDropped, c.event_id);

    // Stop in-flight chunk.
    if (audioEl) {
      try { audioEl.pause && audioEl.pause(); } catch (_) { /* noop */ }
      try { audioEl.removeAttribute && audioEl.removeAttribute('src'); }
      catch (_) { /* noop */ }
      try { audioEl.load && audioEl.load(); } catch (_) { /* noop */ }
    }
    revokeActiveUrl();
    activeChunk = null;
  }

  /**
   * The event_id of the chunk currently playing, or the last one played.
   * Returns -1 if nothing has ever played.
   */
  function getPlayedEventId() {
    return playedEventId;
  }

  /** Read the gate value (primarily for tests/diagnostics). */
  function getInterruptTimestamp() {
    return lastInterruptTimestamp;
  }

  /** Read the queued chunk count (primarily for tests/diagnostics). */
  function getQueueLength() {
    return queue.length;
  }

  /** Tear down completely — detach listeners, drop queue, stop playback. */
  function destroy() {
    if (destroyed) return;
    destroyed = true;
    clear();
    if (audioEl && typeof audioEl.removeEventListener === 'function') {
      audioEl.removeEventListener('ended', handleEnded);
      audioEl.removeEventListener('error', handleError);
    }
  }

  return {
    push,
    clear,
    bumpInterruptTimestamp,
    getPlayedEventId,
    getInterruptTimestamp,
    getQueueLength,
    destroy,
  };
}

// ──────────────────────────────────────────────────────────────────────────
// Internals
// ──────────────────────────────────────────────────────────────────────────

function resolveAudioElement(explicit) {
  if (explicit) return explicit;
  if (typeof document === 'undefined') return null;  // SSR / node tests
  try {
    const el = document.createElement('audio');
    el.setAttribute('playsinline', '');
    el.autoplay = true;
    el.style.display = 'none';
    // Caller is responsible for attaching this to the DOM if they want the
    // browser to actually play it. In practice the consumer wires their own
    // element via opts.audioElement (see livekitClient.js onAgentAudioChunk).
    return el;
  } catch (_) {
    return null;
  }
}
